import React, { useEffect, useState } from 'react';
import NxtbnDrawer from '../drawer';
import { useMutation, useQuery } from '@apollo/client';
import { handleRetriveError } from '../../utils';
import gql from 'graphql-tag';
import { toast } from 'react-toastify';
import NxtbnModal from '../Modal';



interface PermissionHierarchyModalProps {
    isOpen: boolean;
    onClose: () => void;
  }
  
export function PermissionHierarchyModal({ isOpen, onClose }: PermissionHierarchyModalProps) {
    return (
      <NxtbnModal isOpen={isOpen} onClose={onClose}>
        <div className="bg-white rounded-lg shadow-lg w-full p-6">
          <h2 className="text-lg font-semibold text-gray-900">
            Dashboard User Permission Hierarchy
          </h2>
          <div className="mt-4 space-y-4 text-sm text-gray-700">
            <div>
              <span className="font-semibold text-green-600">Superuser:</span>
              <p>Grants unrestricted access to all dashboard functionalities without limitations.</p>
              <p>Inherits all permissions of <span className="font-medium">Store Admin</span> by default.</p>
            </div>
            <div>
              <span className="font-semibold text-green-600">Store Admin:</span>
              <p>Possesses extensive permissions and authority within the dashboard.</p>
              <p>Can perform nearly all operations, with a few critical ones reserved for the superuser.</p>
              <p>Inherits all permissions assigned to other users, except those exclusive to the superuser.</p>
            </div>
            <div>
              <span className="font-semibold text-green-600">Store Staff:</span>
              <p>Has limited access to the dashboard.</p>
              <p>Permissions can be extended granularly by assigning specific permissions managed by the store admin or superuser.</p>
              <p>By default, store staff can view various types of data, but changes can be restricted unless specific permissions are granted. Store staff do not inherit any permissions by default.</p>
            </div>
            <div className="border-t pt-3">
              <p className="text-xs text-gray-500">
                <strong>Additional Notes:</strong>
              </p>
              <ul className="text-xs list-disc pl-5 space-y-1">
                <li>All users have read permissions by default, except for critical data requiring explicit authorization.</li>
                <li>The superuser inherits all permissions of a store admin.</li>
                <li>A store admin inherits all permissions granted to other users by default.</li>
              </ul>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md shadow-sm hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </NxtbnModal>
    );
  }


const formatPermissionName = (name: string) => {
    return name.toLowerCase().startsWith("can ") 
        ? "Permitted to " + name.slice(4) 
        : name;
};


interface Permission {
    codename: string;
    hasAssigned: boolean;
    name: string;
}

interface UserDetailsProps {
    isOpen: boolean;
    onClose: () => void;
    id: number;
}

interface TogglePermissionResponse {
    togglePermission: {
        message: string;
        success: boolean;
    };
}

interface PermissionsData {
    permissions: Permission[];
}

const GET_USER_PERMISSIONS = gql`
    query MyQuery($userId: Int!, $search: String!) {
        permissions(search: $search, userId: $userId) {
            codename
            hasAssigned
            name
        }
    }
`;

const ATTACH_PERMISSIONS_TO_USER = gql`
    mutation togglePermission($permissionCodename: String!, $userId: Int!) {
        togglePermission(permissionCodename: $permissionCodename, userId: $userId) {
            message
            success
        }
    }
`;

const categories = [
    { id: 'product', label: 'Product' },
    { id: 'order', label: 'Order' },
    { id: 'warehouse', label: 'Warehouse' },
    { id: 'stock', label: 'Stock' },
    { id: 'customer', label: 'Customer' },
    { id: 'supplier', label: 'Supplier' },
    { id: 'invoice', label: 'Invoice' },
    { id: 'shipping', label: 'Shipping' },
    { id: 'returns', label: 'Returns' },
    { id: 'settings', label: 'Settings' },
    { id: 'user', label: 'Users' },
    { id: 'payment', label: 'Payments' },
];

function UserPermissions({ isOpen, onClose, id }: UserDetailsProps) {
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [activeCategory, setActiveCategory] = useState('product');
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [loadingPermission, setLoadingPermission] = useState<string | null>(null);

    const { loading, error, data, refetch } = useQuery<PermissionsData>(GET_USER_PERMISSIONS, {
        variables: { userId: id, search: activeCategory },
        onCompleted: (data) => {
            setPermissions(data.permissions);
        },
        onError: handleRetriveError,
    });

    const [togglePermission] = useMutation<TogglePermissionResponse>(ATTACH_PERMISSIONS_TO_USER, {
        onCompleted: (response) => {
            if (response.togglePermission.success) {
                toast.success(response.togglePermission.message || "Permission updated successfully!");
            }
            setLoadingPermission(null);
        },
        onError: (error) => {
            toast.error(error.message || "Something went wrong!");
            setLoadingPermission(null);
        },
    });

    const handlePermissionChange = async (codename: string, hasAssigned: boolean) => {
        setLoadingPermission(codename);
        setPermissions((prevPermissions) =>
            prevPermissions.map((permission) =>
                permission.codename === codename
                    ? { ...permission, hasAssigned }
                    : permission
            )
        );

        await togglePermission({
            variables: {
                userId: id,
                permissionCodename: codename,
            },
        });
    };

    useEffect(() => {
        if (isOpen) {
            refetch({ userId: id, search: activeCategory });
        }
    }, [activeCategory, isOpen, id, refetch]);

    return (
        <NxtbnDrawer size="lg" title="Access Control" isOpen={isOpen} onClose={onClose}>
            <div className="flex h-full">
                {/* Categories Sidebar */}
                <div className="w-48 bg-gray-50 border-r border-gray-200 overflow-y-auto">
                    {categories.map(category => (
                        <button
                            key={category.id}
                            onClick={() => setActiveCategory(category.id)}
                            className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors duration-200 
                                ${activeCategory === category.id 
                                    ? 'bg-green-50 text-green-600 border-r-2 border-green-600' 
                                    : 'text-gray-600 hover:bg-gray-100'}`}
                        >
                            {category.label}
                        </button>
                    ))}
                </div>

                {/* Permissions Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    <button 
                        onClick={() => setIsHelpOpen(true)}
                        className="mb-4 px-4 py-2 bg-green-600 text-white rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                        Access Control Hierarchy Guide
                    </button>

                    <PermissionHierarchyModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />

                    {loading ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="w-6 h-6 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    ) : error ? (
                        <div className="text-center py-8 text-red-600">
                            Error fetching permissions
                        </div>
                    ) : permissions.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">
                            No permissions found for {categories.find(c => c.id === activeCategory)?.label.toLowerCase()}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {permissions.map((permission) => (
                                <div
                                    key={permission.codename}
                                    className="flex items-center justify-between p-3 rounded-lg bg-white border border-gray-200 hover:border-gray-300 transition-colors duration-200"
                                >
                                    <div className="text-sm font-medium text-gray-700">
                                        {formatPermissionName(permission.name)}
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        {loadingPermission === permission.codename && (
                                            <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                                        )}
                                        <input
                                            type="checkbox"
                                            checked={permission.hasAssigned}
                                            onChange={() => handlePermissionChange(
                                                permission.codename,
                                                !permission.hasAssigned
                                            )}
                                            disabled={loadingPermission === permission.codename}
                                            className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </NxtbnDrawer>
    );
}

export default UserPermissions;